import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import MobileNavbar from './components/MobileNavbar';
import ScrollToTop from './components/ScrollRestoration';
import './tailwind-fallback.css';
import AppStoreBanner from "./components/AppStoreBanner";
import GolferSidebar from "./components/GolferSidebar";

const CourseList = lazy(() => import('./CourseList'));
const CourseDetail = lazy(() => import('./CourseDetail'));
const About = lazy(() => import('./components/About'));
const Contact = lazy(() => import('./components/Contact'));
const Courses = lazy(() => import('./components/Courses'));
const Register = lazy(() => import('./components/Register'));
const Login = lazy(() => import('./components/Login'));
const Profile = lazy(() => import('./components/Profile'));
const ProfileSetup = lazy(() => import('./components/ProfileSetup'));
const UnifiedFeed = lazy(() => import('./components/MessageBoard/UnifiedFeed'));
const PostDetail = lazy(() => import("./components/MessageBoard/PostDetail"));
const MessageBoard = lazy(() => import("./components/MessageBoard/MessageBoard"));
const Logout = lazy(() => import("./components/Logout"));
const ForumPostDetail = lazy(() => import("./components/MessageBoard/ForumPostDetail"));
const MyIQ = lazy(() => import("./components/MyIQ"));
const Chat = lazy(() => import("./components/Chat"));
const NotificationsPage = lazy(() => import("./components/NotificationsPage"));
const PublicProfile = lazy(() => import("./components/PublicProfile"));
const LandingPage = lazy(() => import("./components/LandingPage"));
const ContributionsPage = lazy(() => import("./components/ContributionsPage"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const PrivacyChoices = lazy(() => import("./components/PrivacyChoices"));
const PublicFinancials = lazy(() => import("./PublicFinancials"));
const Success = lazy(() => import("./components/Success"));
const Cancel = lazy(() => import("./components/Cancel"));
const Checkout = lazy(() => import("./components/Checkout"));
const WelcomeBack = lazy(() => import("./components/WelcomeBack"));
const UpdatePayment = lazy(() => import("./components/UpdatePayment"));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ForgotUsername = lazy(() => import('./components/ForgotUsername'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ReferralDashboard = lazy(() => import("./components/ReferralDashboard"));
const ClubLoginPage = lazy(() => import("./components/club_interface/ClubLoginPage"));
const ClubDashboard = lazy(() => import("./components/club_interface/ClubDashboard"));
const DevLogin = lazy(() => import("./components/club_interface/DevLogin"));
const PartnersDiscountsPage = lazy(() => import("./components/PartnersDiscountsPage"));
const ModernTable = lazy(() => import("./components/ModernTable"));
const MemberPortalRouter = lazy(() => import("./components/MemberPortal/MemberPortalRouter"));
const Unsubscribe = lazy(() => import("./components/Unsubscribe"));
const UnsubscribeSuccess = lazy(() => import("./components/UnsubscribeSuccess"));
const ClubLandingPage = lazy(() => import("./components/club_interface/ClubLandingPage"));
const AcceptInvitation = lazy(() => import("./components/club_interface/AcceptInvitation"));
const AdminInviteRegister = lazy(() => import("./components/club_interface/AdminInviteRegister"));
const Fellowship = lazy(() => import("./components/Fellowship"));
const AgentRegister = lazy(() => import("./components/real_estate/AgentRegister"));
const AgentLogin = lazy(() => import("./components/real_estate/AgentLogin"));
const AgentPending = lazy(() => import("./components/real_estate/AgentPending"));
const AgentDashboard = lazy(() => import("./components/real_estate/AgentDashboard"));
const AgentProfile = lazy(() => import("./components/real_estate/AgentProfile"));

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

const LegacyUnsubscribeRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/unsubscribe${location.search}`} replace />;
};

// Wrapper component to conditionally render navbars based on route
function AppContent() {
  const location = useLocation();
  const token = localStorage.getItem('access_token');

  // Hide navbars on ClubDashboard routes (they have their own sidebar navigation)
  const isClubDashboard = location.pathname.startsWith('/myclub/dashboard');
  const isFellowship = location.pathname === '/fellowship';
  const isClubAdminInvite = location.pathname.startsWith('/club-admin/');
  const isClubRegister = location.pathname === '/myclub/register';
  const isClubLogin = location.pathname === '/myclub/login';
  const isProfileSetup = location.pathname === '/profile-setup';
  const isCheckout = location.pathname === '/checkout';
  const isWelcomeBack = location.pathname === '/welcome-back';
  const isUpdatePayment = location.pathname === '/update-payment';
  const isAgentPortal = location.pathname.startsWith('/agents');

  // Golfer auth pages with their own header/footer (GolferAuthLayout)
  const golferAuthPaths = ['/login', '/register', '/contact', '/forgot-password', '/forgot-username'];
  const isGolferAuthPage = golferAuthPaths.includes(location.pathname);

  // Pages where the golfer sidebar should NOT appear (public/auth/special pages)
  const sidebarHiddenPaths = [
    '/', '/home', '/login', '/register', '/logout', '/fellowship',
    '/forgot-password', '/forgot-username', '/reset-password',
    '/termsofservice', '/privacypolicy', '/privacy-choices',
    '/contact', '/checkout', '/welcome-back', '/update-payment', '/success', '/cancel', '/profile-setup',
    '/myclub/login', '/myclub/register',
    '/unsubscribe', '/unsubscribe/success',
  ];
  const sidebarHiddenPrefixes = ['/myclub/dashboard', '/club-admin/', '/member-portal', '/agents'];
  const isSidebarHidden =
    !token ||
    sidebarHiddenPaths.includes(location.pathname) ||
    sidebarHiddenPrefixes.some(prefix => location.pathname.startsWith(prefix));
  const showSidebar = !isSidebarHidden;

  return (
    <>
      <ScrollToTop />
      {!isClubRegister && !isClubLogin && !isGolferAuthPage && !isProfileSetup && !isCheckout && !isWelcomeBack && !isUpdatePayment && !isAgentPortal && <AppStoreBanner />}
      {!isClubDashboard && !isFellowship && !isClubAdminInvite && !isClubRegister && !isClubLogin && !isGolferAuthPage && !isProfileSetup && !isCheckout && !isWelcomeBack && !isUpdatePayment && !isAgentPortal && <Navbar />}
      {showSidebar && <GolferSidebar />}
      <div className={showSidebar ? 'has-golfer-sidebar' : ''}>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Default route - Redirect to CourseList if logged in, otherwise to LandingPage */}
        <Route
            path="/"
            element={token ? <Navigate to="/home" replace /> : <LandingPage />}
        />

        {/* Public Routes */}

        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/fellowship" element={<Fellowship />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cancel" element={<Cancel />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/forgot-username" element={<ForgotUsername />} />
        <Route path="/termsofservice" element={<TermsOfService />} />
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
        <Route path="/privacy-choices" element={<PrivacyChoices />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/welcome-back" element={<WelcomeBack />} />
        <Route path="/update-payment" element={<UpdatePayment />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/myclub/dev-login" element={<DevLogin />} />
        <Route path="/myclub/login" element={<ClubLoginPage />} />
        <Route path="/myclub/register" element={<ClubLandingPage />} />
        <Route path="/club-admin/accept-invite/:token" element={<AcceptInvitation />} />
        <Route path="/club-admin/register/:token" element={<AdminInviteRegister />} />
        <Route path="/api/unsubscribe" element={<LegacyUnsubscribeRedirect />} />
        <Route path="/api/unsubscribe/" element={<LegacyUnsubscribeRedirect />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/unsubscribe/success" element={<UnsubscribeSuccess />} />

        {/* Semi-protected routes */}
        <Route
        path="/profile-setup"
        element={
          <ProtectedRoute>
            <ProfileSetup />
          </ProtectedRoute>
        }
        />
        <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        }
        />

        {/* Protected Routes */}
         <Route
        path="/member-portal/*"
        element={
                <ProtectedRoute>
              <MemberPortalRouter />
                </ProtectedRoute>
          }
         />




        <Route
        path="/myclub/dashboard"
        element={
                <ProtectedRoute>
              <ClubDashboard />
                </ProtectedRoute>
          }
         />
        <Route
        path="/club/:clubSlug"
        element={
            <ProtectedRoute>
            <CourseDetail />
            </ProtectedRoute>
        }
        />
        <Route
        path="/club/:clubId"
        element={
                <ProtectedRoute>
              <CourseDetail />
                </ProtectedRoute>
          }
         />
          <Route
          path="/myclub/dashboard/:clubId"
          element={
                <ProtectedRoute>
              <ClubDashboard />
                </ProtectedRoute>
          }
         />
            <Route
          path="/referral"
          element={
            <ProtectedRoute>
              <ReferralDashboard />
            </ProtectedRoute>
          }
         />
        <Route
          path="/about"
          element={
            <ProtectedRoute>
              <About />
            </ProtectedRoute>
          }
         />
          <Route
          path="/public"
          element={
            <ProtectedRoute>
              <PublicFinancials />
            </ProtectedRoute>
          }
         />
          <Route
          path="/myiq"
          element={
                <ProtectedRoute>
              <MyIQ />
                </ProtectedRoute>
          }
          />
        <Route
          path="/Feed"
          element={
            <ProtectedRoute>
              <UnifiedFeed />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <UnifiedFeed />
            </ProtectedRoute>
          }
        />
        <Route
          path="/post/:id"
          element={
            <ProtectedRoute>
              <PostDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messageboard"
          element={
            <ProtectedRoute>
              <MessageBoard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact"
          element={

              <Contact />

          }
        />
        <Route
          path="/courses"
          element={
            <ProtectedRoute>
              <Courses />
            </ProtectedRoute>
          }
      />
       <Route
            path="/memberprofile/:username"
            element={
             <ProtectedRoute>
                <PublicProfile />
             </ProtectedRoute>
          }
        />

        {/* Main Route for Course List */}
        <Route
          path="/courselist"
          element={
            <ProtectedRoute>
              <CourseList />
            </ProtectedRoute>
          }
        />

        {/* Dynamic Route for Individual Course Details */}
        <Route
          path="/course/:id"
          element={
            <ProtectedRoute>
              <CourseDetail />
            </ProtectedRoute>
          }
        />
  <Route
    path="/forums"
    element={<Navigate to="/feed" replace />}
    />
  <Route
    path="/forums/usa/:id"
    element={
      <ProtectedRoute>
        <ForumPostDetail />
      </ProtectedRoute>
    }
    />
  <Route
    path="/forums/:state/:id"
    element={
      <ProtectedRoute>
        <ForumPostDetail />
      </ProtectedRoute>
    }
  />
          <Route
    path="/myinfo"
    element={
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    }
  />
  <Route
    path="/forums/:stateCode"
    element={<Navigate to="/feed" replace />}
  />
          <Route
    path="/memberprofile/:username"
    element={
      <ProtectedRoute>
        <PublicProfile isOwnProfile={false} />
      </ProtectedRoute>
    }
  />
          <Route
    path="/memberprofile"
    element={
      <ProtectedRoute>
        <PublicProfile isOwnProfile={true} />
      </ProtectedRoute>
    }
  />
          <Route
    path="/forum"
    element={<Navigate to="/feed" replace />}
    />
              <Route
    path="/contributions"
    element={
      <ProtectedRoute>
        <ContributionsPage />
      </ProtectedRoute>
    }
    />
  <Route
    path="/chat"
    element={
      <ProtectedRoute>
        <Chat />
      </ProtectedRoute>
    }
  />
  <Route
    path="/notifications"
    element={
      <ProtectedRoute>
        <NotificationsPage />
      </ProtectedRoute>
    }
  />
            <Route
    path="/partner-discounts"
    element={
      <ProtectedRoute>
        <PartnersDiscountsPage />
      </ProtectedRoute>
    }
  />
                       <Route
    path="/feestable"
    element={
      <ProtectedRoute>
        <ModernTable />
      </ProtectedRoute>
    }

  />
        {/* Real Estate Agent Portal */}
        <Route path="/agents" element={<Navigate to="/agents/login" replace />} />
        <Route path="/agents/register" element={<AgentRegister />} />
        <Route path="/agents/login" element={<AgentLogin />} />
        <Route path="/agents/pending" element={<ProtectedRoute><AgentPending /></ProtectedRoute>} />
        <Route path="/agents/dashboard" element={<ProtectedRoute><AgentDashboard /></ProtectedRoute>} />
        <Route path="/agents/profile" element={<ProtectedRoute><AgentProfile /></ProtectedRoute>} />

        {/* Catch-All Route */}
        <Route path="*" element={<Navigate to="/" />}/>
      </Routes>
      </Suspense>
      </div>
      {!isClubDashboard && !isFellowship && !isClubRegister && !isClubLogin && !isGolferAuthPage && !isProfileSetup && !isCheckout && !isWelcomeBack && !isUpdatePayment && !isAgentPortal && <MobileNavbar />}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
